import { useEffect } from 'react'

import { Code } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import { Alert } from '@enechain/ecloud-designsystem'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { RegionLoading } from '~/components/common/RegionLoading'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { todayCivilDate } from '~/models/civilDate'
import { SubmitterMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/components/SubmitterMasterFormFields'
import { useGetSubmitterMaster } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useGetSubmitterMaster'
import type { SubmitterMasterRow } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { useUpdateSubmitterMaster } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useUpdateSubmitterMaster'
import {
  type SubmitterMasterFormInput,
  SubmitterMasterFormSchema,
  type SubmitterMasterFormValues,
} from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/models/schema'
import { resolveSubmitterMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/models/serverError'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'

type Props = {
  isOpen: boolean
  row: SubmitterMasterRow
  onClose: () => void
}

const SUBMITTER_EDIT_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.AlreadyExists]:
    'page:adminOcctoMaster.submitterMaster.serverError.alreadyExists',
  [Code.Aborted]: 'page:adminOcctoMaster.submitterMaster.serverError.aborted',
  [Code.NotFound]: 'page:adminOcctoMaster.submitterMaster.serverError.notFound',
  [Code.InvalidArgument]:
    'page:adminOcctoMaster.submitterMaster.serverError.invalidArgument',
}

export const EditSubmitterMasterModal = ({
  isOpen,
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdateSubmitterMaster()
  const {
    data,
    isPending: isFetching,
    isError,
    error,
  } = useGetSubmitterMaster(row.id)
  const fetched = data?.submitterMaster

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    reset,
    setValue,
    isDirty,
  } = useCrudModalForm<SubmitterMasterFormInput, SubmitterMasterFormValues>({
    schema: SubmitterMasterFormSchema,
    defaultValues: {
      submitterCode: '',
      submitterName: '',
      licenseType: row.licenseType,
      areaCode: row.areaCode,
      businessMasterId: '',
      note: '',
      validFrom: todayCivilDate(),
      validTo: undefined,
      members: [],
    },
    onSubmit: async (values) => {
      if (fetched?.updatedAt === undefined) {
        throw new Error(
          t('page:adminOcctoMaster.submitterMaster.serverError.unknown'),
        )
      }
      await mutateAsync({
        id: row.id,
        submitterName: values.submitterName,
        businessMasterId: values.businessMasterId,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        // valid_from は update request で required (proto field 8, mutable)。
        // 送信し忘れると BFF protovalidate が InvalidArgument を返し編集不可
        // になる (issue #5913)。
        validFrom: values.validFrom,
        validTo: values.validTo,
        members: values.members.map((m) => ({
          bgMasterId: m.bgMasterId,
          bgName: m.bgName,
          validFrom: m.validFrom,
          validTo: m.validTo,
        })),
        expectedUpdatedAt: fetched.updatedAt,
      })
    },
    onClose,
    customErrorKeys: SUBMITTER_EDIT_ERROR_KEYS,
    unknownErrorKey:
      'page:adminOcctoMaster.submitterMaster.serverError.unknown',
    resolveDetailedError: resolveSubmitterMasterDetailedError,
  })

  useEffect(() => {
    if (fetched !== undefined) {
      reset({
        submitterCode: fetched.submitterCode,
        submitterName: fetched.submitterName,
        licenseType: fetched.licenseType,
        areaCode: fetched.areaCode,
        businessMasterId: fetched.businessMasterId,
        note: fetched.note ?? '',
        validFrom:
          fetched.validFrom !== undefined
            ? {
                year: fetched.validFrom.year,
                month: fetched.validFrom.month,
                day: fetched.validFrom.day,
              }
            : todayCivilDate(),
        validTo:
          fetched.validTo !== undefined
            ? {
                year: fetched.validTo.year,
                month: fetched.validTo.month,
                day: fetched.validTo.day,
              }
            : undefined,
        members: fetched.members.map((m) => ({
          bgMasterId: m.bgMasterId,
          bgName: m.bgName,
          validFrom:
            m.validFrom !== undefined
              ? {
                  year: m.validFrom.year,
                  month: m.validFrom.month,
                  day: m.validFrom.day,
                }
              : todayCivilDate(),
          validTo:
            m.validTo !== undefined
              ? {
                  year: m.validTo.year,
                  month: m.validTo.month,
                  day: m.validTo.day,
                }
              : undefined,
        })),
      })
    }
  }, [fetched, reset])

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      isSaveDisabled={isFetching || isError}
      onClose={handleClose}
      serverError={serverError}
      // design (`design/admin/occto.html` #sm-edit-modal) は `.ecl-modal--lg`。
      size="lg"
      title={t('page:adminOcctoMaster.submitterMaster.modal.editTitle')}
    >
      {isError && (
        <Alert
          status="error"
          title={mapMasterGetError(
            t,
            error,
            'page:adminOcctoMaster.submitterMaster.serverError',
          )}
        />
      )}
      <RegionLoading isLoading={isFetching}>
        {isFetching ? null : (
          <SubmitterMasterFormFields
            control={control}
            errors={errors}
            isEdit
            setValue={setValue}
          />
        )}
      </RegionLoading>
    </CrudModalLayout>
  )
}
